// Length limits a COLUMN declares — the one place the client reads them.
//
// `maxTextLength` mirrors the engine's `_max_text_length` (CHAR/VARCHAR db_size)
// and `minTextLength` / `textLengthForMin` mirror `_min_text_length` /
// `_text_length_for_min` (the optional `min_length` property). The form's
// live counter and the content panel's checks both read these, so neither can
// disagree with what a save will accept.

import type { ColumnDef } from './types'
import { richPlainText } from '../richContent'

const BOUNDED_TEXT_TYPES = ['CHAR', 'VARCHAR', 'VARCHAR2', 'CHARACTER', 'CHARACTER VARYING']

/** First dimension of db_size for CHAR/VARCHAR columns, else null. */
export function maxTextLength(col: Partial<ColumnDef> | undefined): number | null {
  if (!col) return null
  const ct = (col.col_type || '').toString().trim().toUpperCase()
  if (!BOUNDED_TEXT_TYPES.includes(ct)) return null
  const raw = (col.db_size || '').toString().trim()
  if (!raw) return null
  const first = raw.includes(',') ? raw.split(',')[0].trim() : raw
  const n = Number(first)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.floor(n)
}

/** The column's authored `min_length`; null when it has none (blank, 0, junk). */
export function minTextLength(col: Partial<ColumnDef> | undefined): number | null {
  const raw = col?.min_length
  if (raw == null || raw === '' || typeof raw === 'boolean') return null
  const n = Math.trunc(Number(String(raw).trim()))
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Characters a minimum counts: visible text of rich text, trimmed text otherwise. */
export function textLengthForMin(col: Partial<ColumnDef> | undefined, value: unknown): number {
  if (value == null) return 0
  if ((col?.col_type || '').toString().trim().toUpperCase() === 'RICHTEXT') {
    return richPlainText(value).length
  }
  return String(value).trim().length
}
